<script setup lang="ts">
import { Check, Copy, KeyRound, TriangleAlert } from 'lucide-vue-next'

import type { ApiKeyTokenCreated } from '@/types/apiKeys'

const props = defineProps<{
  open: boolean
  token: ApiKeyTokenCreated | null
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

const { t } = useI18n()
const { copyToClipboard } = useDOM()
const { localDate } = useDate()

const copied = ref(false)
const acknowledged = ref(false)

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      copied.value = false
      acknowledged.value = false
    }
  }
)

const expiresLabel = computed(() => {
  if (!props.token?.expiresAt) return t('pages.settings.apiKeys.reveal.noExpiry')
  return localDate(new Date(props.token.expiresAt))
})

const copySecret = async () => {
  if (!props.token) return
  await copyToClipboard(props.token.secret)
  copied.value = true
}

const finish = () => {
  if (!acknowledged.value) return
  emit('update:open', false)
}
</script>

<template>
  <BaseModal
    :is-open="open"
    :heading-title="t('pages.settings.apiKeys.reveal.title', { name: token?.name })"
    :is-closable="false"
    size="lg"
    :link-text="t('pages.settings.apiKeys.reveal.finish')"
    :is-disabled="!acknowledged"
    @click="finish"
  >
    <template #customHeadingTitle>
      <div class="flex flex-col items-center gap-2 text-center">
        <span class="flex size-11 items-center justify-center rounded-full bg-green-100 text-green-700">
          <KeyRound class="size-6" />
        </span>
        <p class="text-base font-semibold text-foreground">
          {{ t('pages.settings.apiKeys.reveal.title', { name: token?.name }) }}
        </p>
        <p class="text-sm text-muted-foreground">{{ t('pages.settings.apiKeys.reveal.subtitle') }}</p>
      </div>
    </template>

    <div class="flex flex-col gap-6">
      <Alert variant="destructive">
        <TriangleAlert />
        <AlertDescription>
          <strong>{{ t('pages.settings.apiKeys.reveal.warningStrong') }}</strong>
          {{ t('pages.settings.apiKeys.reveal.warningRest') }}
        </AlertDescription>
      </Alert>

      <div class="flex flex-col gap-2">
        <Label>{{ t('pages.settings.apiKeys.reveal.secretLabel') }}</Label>
        <div class="flex flex-wrap items-stretch gap-2">
          <div
            class="min-w-0 flex-1 select-all rounded-md border bg-muted px-3 py-2.5 font-mono text-sm break-all text-foreground"
          >
            {{ token?.secret }}
          </div>
          <Button variant="outline" @click="copySecret">
            <Check v-if="copied" />
            <Copy v-else />
            {{ copied ? t('pages.settings.apiKeys.reveal.copied') : t('pages.settings.apiKeys.reveal.copy') }}
          </Button>
        </div>
      </div>

      <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-t pt-4 text-sm">
        <dt class="text-muted-foreground">{{ t('pages.settings.apiKeys.reveal.expires') }}</dt>
        <dd>{{ expiresLabel }}</dd>
      </dl>

      <label class="flex cursor-pointer items-start gap-2 text-sm text-foreground select-none">
        <Checkbox v-model="acknowledged" class="mt-0.5" />
        {{ t('pages.settings.apiKeys.reveal.acknowledge') }}
      </label>
    </div>
  </BaseModal>
</template>
